// components/AwsPricingCalculator.jsx — tab Kalkulator.
//
// Tiga panel: keranjang (kiri) · editor baris (tengah) · matriks komitmen (kanan).
//
// MATRIKS KOMITMEN adalah alasan halaman ini ada. AWS Pricing Calculator
// memaksa pengguna memilih satu opsi komitmen lalu melihat satu angka; untuk
// membandingkan RI 1yr All Upfront dengan SP 3yr No Upfront ia harus membuat
// dua estimate dan membandingkannya di kepala. Di sini 24 sel tampil
// berdampingan dengan Upfront / $/bln / Total 1yr / Hemat %.
//
// PEMBAGIAN KERJA. Backend mengembalikan harga ternormalisasi (POST
// /api/aws/calculator/rates); berkas ini hanya mengalikan dengan kuantitas dan
// menambah Support & VAT. Mengubah jumlah jam TIDAK memanggil API.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import {
  Plus, Trash2, ShoppingCart, AlertTriangle, Loader2, Server, HardDrive,
  ChevronRight, Info, History, Database, Search,
} from 'lucide-react';
import AwsPricingEstimates from './AwsPricingEstimates';
import AwsPricingExport from './AwsPricingExport';
import SearchableSelect from './SearchableSelect';

const REGIONS = [
  { code: 'ap-southeast-3', label: 'Jakarta (ap-southeast-3)' },
  { code: 'ap-southeast-1', label: 'Singapore (ap-southeast-1)' },
];

const LEASE_YEARS = (lease) => (lease === '3yr' ? 3 : 1);

/**
 * Pengelompokan tab untuk baris usage.
 *
 * Kuncinya `job_key`, BUKAN `service_code`. EBS tidak punya service_code
 * sendiri — ia berada di AmazonEC2 bersama instance dan hanya dipisahkan oleh
 * instance_type IS NULL, jadi mengelompokkan per service akan mencampurnya
 * dengan S3 dan RDS. job_key juga memisahkan hal yang memang beda sifat:
 * s3-storage (per GB-bulan) dari s3-requests (per permintaan), rds-storage dari
 * rds-iops.
 *
 * Tiga tab, bukan satu tab per service. Probe 02-09-2026 di Jakarta: 1.172
 * baris usage tersebar di 16 job, dan sebarannya sangat tidak rata —
 * data-transfer sendiri 565 (48%) dan RDS 374 (32%). Satu tab per service
 * berarti belasan tab yang sebagian hanya berisi 4 baris; satu tab untuk
 * semuanya berarti dropdown 1.172 baris. Tiga tab + penyaring kategori
 * menempatkan pemisahannya di tempat yang sebanding dengan datanya.
 *
 * Job yang tidak terdaftar di sini tetap muncul, ditampung tab 'other' —
 * supaya job sync baru (mis. NAT Gateway) tidak hilang dari UI sampai
 * seseorang ingat memperbarui peta ini.
 */
const USAGE_GROUPS = {
  storage: ['ebs-storage', 'ebs-snapshot', 'ebs-iops', 's3-storage', 's3-requests',
            'rds-storage', 'rds-snapshot', 'rds-iops', 'opensearch-volume'],
  network: ['data-transfer', 'elb-application', 'nat-gateway', 'vpc',
            'route53-global', 'route53-regional'],
  monitoring: ['cloudwatch', 'cloudtrail'],
};
const USAGE_TABS = ['storage', 'network', 'monitoring', 'other'];
const groupOfJob = (jobKey) => {
  for (const [g, keys] of Object.entries(USAGE_GROUPS)) if (keys.includes(jobKey)) return g;
  return 'other';
};
// Label kategori: job_key mentah ('ebs-iops') tidak enak dibaca di dropdown.
const JOB_LABELS = {
  'ebs-storage': 'EBS — volume', 'ebs-snapshot': 'EBS — snapshot', 'ebs-iops': 'EBS — IOPS & throughput',
  's3-storage': 'S3 — storage', 's3-requests': 'S3 — requests & retrieval',
  'rds-storage': 'RDS — storage', 'rds-snapshot': 'RDS — backup & snapshot', 'rds-iops': 'RDS — IOPS',
  'opensearch-volume': 'OpenSearch — EBS volume',
  'data-transfer': 'Data transfer', 'elb-application': 'Application Load Balancer',
  'nat-gateway': 'NAT Gateway',
  'vpc': 'VPC — IPv4, endpoint, VPN', 'route53-global': 'Route 53 — global', 'route53-regional': 'Route 53 — regional',
  'cloudwatch': 'CloudWatch', 'cloudtrail': 'CloudTrail',
};
const jobLabel = (k) => JOB_LABELS[k] || k || '—';

// Label tab memakai i18n bila key-nya ada, jika tidak jatuh ke teks Inggris.
// Mencegah key mentah bocor ke UI bila translations.js belum diperbarui.
const tabFallback = { storage: 'Storage', network: 'Network', monitoring: 'Monitoring', other: 'Other' };

// ─────────────────────────────────────────────────────────────────────────────
// ARITMATIKA
//
// Fungsi-fungsi di bawah murni dan tidak menyentuh state, supaya perilakunya
// bisa dibaca tanpa menelusuri komponen.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Biaya bulanan untuk baris tiered (S3, EBS, Data Transfer).
 *
 * Menjumlahkan LINTAS TIER, bukan memakai tier pertama untuk seluruh volume.
 * S3 General Purpose di ap-southeast-3 punya tiga tier (0.025 / 0.024 / 0.023);
 * memakai satu tier untuk 100 TB menghasilkan angka yang salah tanpa terlihat
 * sebagai galat.
 */
export function tieredCost(volume, tiers) {
  const v = Number(volume) || 0;
  if (!v || !Array.isArray(tiers) || !tiers.length) return 0;
  let total = 0;
  for (const t of tiers) {
    const begin = Number(t.begin) || 0;
    if (v <= begin) break;
    const end = t.end === null || t.end === undefined ? Infinity : Number(t.end);
    const portion = Math.min(v, end) - begin;
    if (portion > 0) total += portion * (Number(t.price_per_unit) || 0);
  }
  return total;
}

/**
 * Support Business: tiered MARGINAL, bukan satu persen atas seluruh tagihan.
 *
 * Marginal berarti tiap tier hanya dikenakan pada porsi yang jatuh di dalam
 * rentangnya. 10% atas seluruh $100k akan melebih-lebihkan biaya support secara
 * mencolok. Minimum diambil dari kolom min_usd pada tier pertama plan tersebut.
 */
export function supportCost(base, tiers, plan) {
  const b = Number(base) || 0;
  const rows = (tiers || []).filter(t => t.plan === plan)
    .sort((a, z) => (Number(a.tier_from) || 0) - (Number(z.tier_from) || 0));
  if (!rows.length) return 0;
  let total = 0;
  for (const t of rows) {
    const from = Number(t.tier_from) || 0;
    if (b <= from) break;
    const to = t.tier_to === null || t.tier_to === undefined ? Infinity : Number(t.tier_to);
    const portion = Math.min(b, to) - from;
    if (portion > 0) total += portion * ((Number(t.percent) || 0) / 100);
  }
  const min = Number(rows[0].min_usd) || 0;
  return Math.max(total, min);
}

/**
 * Hitung satu baris keranjang.
 *
 * Mengembalikan TIGA angka yang sengaja dipisah:
 *   recurring  — biaya berulang bulanan ($/jam × jam × qty)
 *   amortized  — porsi upfront yang dibebankan per bulan
 *   cashUpfront— uang yang keluar di bulan 0
 *
 * Pemisahan ini bukan kosmetik: basis Support MENGECUALIKAN upfront, jadi
 * `recurring` dan `amortized` tidak boleh dijumlahkan sebelum Support dihitung.
 */
export function computeLine(line, hoursPerMonth) {
  const qty = Number(line.qty) || 0;
  const hours = Number(line.hoursPerMonth ?? hoursPerMonth) || 0;
  const r = line.rates;
  if (!r || r.error) return { recurring: 0, amortized: 0, cashUpfront: 0, unavailable: true };

  if (line.kind === 'usage') {
    const monthly = tieredCost(line.volumeGb, r.tiers) * (qty || 1);
    return { recurring: monthly, amortized: 0, cashUpfront: 0, unit: r.tiers?.[0]?.unit };
  }

  const c = line.choice || { kind: 'od' };
  if (c.kind === 'od') {
    const hourly = Number(r.onDemand?.hourly) || 0;
    return { recurring: qty * hourly * hours, amortized: 0, cashUpfront: 0, hourly };
  }

  const cell = findCell(r.matrix, c);
  if (!cell || cell.missing) return { recurring: 0, amortized: 0, cashUpfront: 0, unavailable: true };
  const months = LEASE_YEARS(c.lease) * 12;
  return {
    recurring: qty * (Number(cell.hourly) || 0) * hours,
    amortized: qty * ((Number(cell.upfront) || 0) / months),
    cashUpfront: qty * (Number(cell.upfront) || 0),
    hourly: Number(cell.hourly) || 0,
    derived: cell.derived,
    anomaly: cell.anomaly,
  };
}

export function findCell(matrix, choice) {
  if (!Array.isArray(matrix) || !choice) return null;
  return matrix.find(c =>
    c.kind === choice.kind
    && c.lease_length === choice.lease
    && c.payment === choice.payment
    && (choice.kind === 'ri' ? c.offering === choice.offering : c.plan_type === choice.plan_type)
  ) || null;
}

/**
 * Kunci pilihan untuk baris usage.
 *
 * usage_type saja tidak cukup — lihat catatan di fetchTermsByUsageType() pada
 * awsPricing.js. Dipakai bersama oleh nilai <select> dan pencocokan baliknya,
 * jadi keduanya tidak bisa menyimpang.
 */
const usageKey = (usageType, operation) => `${usageType || ''}|${operation || ''}`;

const money = (v, digits = 2) =>
  (Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });

const idr = (v) => (Number(v) || 0).toLocaleString('id-ID', { maximumFractionDigits: 0 });

let seq = 0;
const newId = () => `l${++seq}-${Date.now()}`;

// ─────────────────────────────────────────────────────────────────────────────

export default function AwsPricingCalculator({ config, tiers, loading, pendingImport, onPendingConsumed, onRegionChange }) {
  const { t } = useLang();
  const hoursPerMonth = Number(config?.map?.hours_per_month) || 730;
  const fxRate = Number(config?.map?.usd_idr_rate) || 0;

  const [region, setRegion] = useState('ap-southeast-3');
  const [lines, setLines] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [vatCountry, setVatCountry] = useState('ID');
  const [supportPlan, setSupportPlan] = useState('business');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // ── Draft baris baru ─────────────────────────────────────────────────────
  const [kind, setKind] = useState('ec2');
  const [opts, setOpts] = useState(null);
  // Daftar PENUH opsi untuk region/kind ini (hasil panggilan tanpa filter).
  // `opts` menyempit mengikuti pilihan; `baseOpts` tidak. Dropdown menampilkan
  // baseOpts supaya pengguna tetap melihat semua opsi — yang tidak berlaku
  // untuk kombinasi sekarang ditampilkan abu-abu, bukan dihilangkan.
  const [baseOpts, setBaseOpts] = useState(null);
  // Fallback i18n lokal — lihat catatan pada tabFallback.
  const tt = (key, fb) => { try { const v = t(key); return (v && v !== key) ? v : fb; } catch (_) { return fb; } };

  const [draft, setDraft] = useState({
    instance_type: '', os: '', tenancy: '', pre_installed_sw: '',
    db_engine: '', db_edition: '', deployment_option: '', license_model: '',
    service_code: '', usage_type: '', operation: '', category: '', qty: 1, volumeGb: 0,
  });

  // `kind` ikut dikirim karena katalog RDS memakai kaskade yang sepenuhnya
  // berbeda dari EC2 (engine/edition/deployment/license, bukan os/tenancy).
  const loadOptions = useCallback(async (params, forKind) => {
    try {
      const res = await api.get('/api/aws/calculator/options', {
        params: { region, kind: forKind || undefined, ...params } });
      setOpts(res.data);
      const isBase = !params || Object.values(params).every((v) => v === undefined || v === '');
      if (isBase) setBaseOpts(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    }
  }, [region]);

  // Berganti region ATAU berganti jenis baris sama-sama membatalkan seluruh
  // draft: pilihan lama hampir pasti tidak ada di katalog yang baru, dan
  // menyisakannya menghasilkan kombinasi yang tampak valid tapi tanpa harga.
  useEffect(() => {
    setOpts(null);
    setBaseOpts(null);
    setDraft(d => ({ ...d, instance_type: '', os: '', tenancy: '', pre_installed_sw: '',
      db_engine: '', db_edition: '', deployment_option: '', license_model: '',
      usage_type: '', operation: '', category: '' }));
    // Hanya rds/opensearch punya kaskade khusus di endpoint options. Tab usage
    // memakai respons default yang sudah memuat usage_types lengkap.
    loadOptions({}, (kind === 'rds' || kind === 'opensearch') ? kind : undefined);
  }, [region, kind, loadOptions]);

  // Opsi EC2 selalu diambil dari daftar penuh region ini. Yang tidak ada pada
  // kombinasi terpilih ditandai `disabled` (abu-abu) — sebelumnya opsi seperti
  // itu HILANG dari daftar, sehingga setelah form terisi lengkap dropdown
  // instance type hanya menyisakan satu entri dan terlihat seperti terkunci.
  //
  // `instance_type` sengaja TIDAK pernah dikunci: memilihnya mengosongkan
  // os/tenancy/pre_installed_sw, jadi kombinasi apa pun selalu bisa dicapai
  // dari sana. Mengabuinya justru membuat form benar-benar buntu.
  const ec2Opts = (field) => {
    const full = (baseOpts && baseOpts[field]) || [];
    const narrow = (opts && opts[field]) || [];
    const avail = new Map(narrow.map((o) => [o.value, o.count]));
    const source = full.length ? full : narrow;
    return source.map((o) => ({
      value: o.value,
      label: o.value,
      count: avail.has(o.value) ? avail.get(o.value) : o.count,
      disabled: field !== 'instance_type' && avail.size > 0 && !avail.has(o.value),
    }));
  };

  // Dropdown bertingkat: setiap pilihan menyempitkan pilihan berikutnya, supaya
  // kombinasi yang tidak ada di katalog tidak bisa dipilih sejak awal.
  const onPick = (field, value) => {
    const next = { ...draft, [field]: value };
    if (field === 'instance_type') { next.os = ''; next.tenancy = ''; next.pre_installed_sw = ''; }
    if (field === 'os') { next.tenancy = ''; next.pre_installed_sw = ''; }
    if (field === 'tenancy') { next.pre_installed_sw = ''; }
    setDraft(next);
    if (field !== 'pre_installed_sw') {
      loadOptions({
        instance_type: next.instance_type || undefined,
        os: next.os || undefined,
        tenancy: next.tenancy || undefined,
      });
    }
  };

  // Kaskade RDS — lima tingkat, urut dari mesin ke ukuran. Memilih tingkat mana
  // pun mengosongkan seluruh tingkat DI BAWAHNYA, karena pilihan lama belum
  // tentu ada dalam himpunan yang baru menyempit.
  const RDS_CASCADE = ['db_engine', 'db_edition', 'deployment_option', 'license_model', 'instance_type'];

  const loadRdsOptions = useCallback((d) => loadOptions({
    db_engine:         d.db_engine || undefined,
    db_edition:        d.db_edition || undefined,
    deployment_option: d.deployment_option || undefined,
    license_model:     d.license_model || undefined,
  }, 'rds'), [loadOptions]);

  const onPickRds = (field, value) => {
    const next = { ...draft, [field]: value };
    const from = RDS_CASCADE.indexOf(field);
    RDS_CASCADE.slice(from + 1).forEach(f => { next[f] = ''; });
    setDraft(next);
    loadRdsOptions(next);
  };

  /**
   * Sebuah tingkat dianggap SELESAI bila sudah dipilih ATAU katalog memang
   * tidak menawarkan pilihan apa pun untuknya.
   *
   * Yang kedua bukan kasus pinggiran: MySQL, PostgreSQL, dan MariaDB tidak
   * punya `databaseEdition` sama sekali, sehingga dropdown Edisi kosong
   * selamanya. Versi pertama fitur ini menuntut setiap tingkat terisi, yang
   * membuat Instance type terkunci permanen untuk ketiga engine itu — pengguna
   * dihadapkan pada dropdown mati tanpa penjelasan.
   */
  const rdsDone = (field) => Boolean(draft[field]) || ((opts?.[field] || []).length === 0);

  /**
   * Isi otomatis tingkat yang hanya punya SATU kemungkinan.
   *
   * Meminta pengguna memilih dari daftar berisi satu item bukan pilihan, itu
   * upacara. MySQL misalnya selalu 'No license required'. Setiap pengisian
   * mempersempit katalog dan memicu putaran berikutnya; berhenti sendiri karena
   * tiap putaran mengisi minimal satu dari lima tingkat.
   *
   * `instance_type` SENGAJA dikecualikan — ukuran instance adalah keputusan
   * inti pengguna, dan memilihkannya walau kebetulan tinggal satu akan
   * menyembunyikan betapa sempitnya sisa katalog.
   */
  useEffect(() => {
    if (kind !== 'rds' || !opts || opts.kind !== 'rds') return;
    for (const f of RDS_CASCADE) {
      if (f === 'instance_type') break;
      if (draft[f]) continue;
      const vals = opts[f] || [];
      if (vals.length === 1) {
        const next = { ...draft, [f]: vals[0].value };
        setDraft(next);
        loadRdsOptions(next);
        return;   // satu perubahan per putaran; efek ini berjalan lagi setelahnya
      }
      if (vals.length > 1) break;   // tunggu pengguna memilih
    }
    // Deps sengaja hanya [kind, opts]. Menambahkan `draft` akan membuat efek
    // ini berjalan pada SETIAP ketikan di form, dan `loadRdsOptions` di deps
    // akan memicunya ulang tiap kali opsi selesai dimuat — keduanya putaran
    // tak berujung. Nilai draft dibaca lewat closure yang selalu segar karena
    // efek ini hanya bertindak setelah `opts` baru tiba.
  }, [kind, opts]);

  const addLine = async () => {
    setError(null);
    const spec = kind === 'ec2'
      ? {
          key: 'draft', kind: 'ec2', region,
          instance_type: draft.instance_type, os: draft.os, tenancy: draft.tenancy,
          pre_installed_sw: draft.pre_installed_sw || 'NA',
        }
      : kind === 'rds'
      ? {
          key: 'draft', kind: 'rds', region,
          instance_type: draft.instance_type, db_engine: draft.db_engine,
          db_edition: draft.db_edition || '', deployment_option: draft.deployment_option,
          license_model: draft.license_model || '',
        }
      : kind === 'opensearch'
      ? { key: 'draft', kind: 'opensearch', region, instance_type: draft.instance_type }
      // Seluruh tab usage (storage/network/monitoring/other) menghasilkan baris
      // ber-kind 'usage'. Pemisahan tab murni soal navigasi UI; computeLine,
      // resolver backend, dan ekspor tetap melihat satu jenis saja.
      : { key: 'draft', kind: 'usage', region, service_code: draft.service_code,
          usage_type: draft.usage_type, operation: draft.operation || '' };

    if (kind === 'ec2' && (!spec.instance_type || !spec.os || !spec.tenancy)) {
      setError(t('awsp.calc.needInstance'));
      return;
    }
    if (kind === 'rds' && (!spec.instance_type || !spec.db_engine || !spec.deployment_option)) {
      setError(t('awsp.calc.needRds'));
      return;
    }
    if (kind === 'opensearch' && !spec.instance_type) {
      setError(t('awsp.calc.needOpenSearch'));
      return;
    }
    if (USAGE_TABS.includes(kind) && !spec.usage_type) {
      setError(t('awsp.calc.needUsageType'));
      return;
    }

    setBusy(true);
    try {
      const res = await api.post('/api/aws/calculator/rates', { lines: [spec] });
      const r = res.data.lines[0];
      if (r.error) { setError(r.error); return; }
      const id = newId();
      setLines(prev => [...prev, {
        id, kind: USAGE_TABS.includes(kind) ? 'usage' : kind, region,
        label: kind === 'ec2'
          ? `${spec.instance_type} · ${spec.os}${spec.pre_installed_sw && spec.pre_installed_sw !== 'NA' ? ` · ${spec.pre_installed_sw}` : ''}`
          : kind === 'rds'
          ? `${spec.instance_type} · ${spec.db_engine}${spec.db_edition ? ` ${spec.db_edition}` : ''} · ${spec.deployment_option}`
          : kind === 'opensearch'
          ? `${spec.instance_type} · OpenSearch`
          : `${draft.service_code} · ${draft.usage_type}${draft.operation ? ` · ${draft.operation}` : ''}`,
        instance_type: spec.instance_type, os: spec.os, tenancy: spec.tenancy,
        pre_installed_sw: spec.pre_installed_sw,
        db_engine: spec.db_engine, db_edition: spec.db_edition,
        deployment_option: spec.deployment_option, license_model: spec.license_model,
        service_code: kind === 'ec2' ? 'AmazonEC2' : kind === 'rds' ? 'AmazonRDS'
          : kind === 'opensearch' ? 'AmazonES' : draft.service_code,
        usage_type: r.resolved?.usage_type || draft.usage_type,
        // Untuk EC2/RDS/OpenSearch operation datang dari resolver backend;
        // untuk baris usage ia berasal dari pilihan pengguna. Satu kunci saja —
        // dua kunci `operation` dalam satu literal objek membuat yang belakangan
        // menimpa yang depan tanpa peringatan.
        operation: r.resolved?.operation || draft.operation || null,
        qty: Number(draft.qty) || 1,
        hoursPerMonth,
        volumeGb: Number(draft.volumeGb) || 0,
        choice: { kind: 'od' },
        rates: r,
      }]);
      setSelectedId(id);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  // ── Penyerapan baris impor (tab Import) ──────────────────────────────────
  //
  // Tab Import adalah komponen SAUDARA di halaman induk, jadi ia tidak bisa
  // memanggil setLines. Induk menyimpan hasilnya di `pendingImport` dan
  // berpindah ke tab ini; efek di bawah menyerapnya saat mount lalu memanggil
  // onPendingConsumed() supaya payload yang sama tidak diserap dua kali.
  //
  // MENGGANTI seluruh keranjang, sama seperti onEstimateLoaded — bukan pilihan
  // gaya: tab yang di-unmount sudah menghapus keranjang sebelumnya, jadi
  // "menambahkan" hanya akan tampak seperti menambah ke sesuatu yang masih ada.
  //
  // Harga TIDAK diambil dari panel impor. Seluruh baris dikirim ke
  // /api/aws/calculator/rates dalam satu permintaan — jalur harga yang sama
  // dengan addLine, sehingga tidak ada dua sumber angka yang bisa menyimpang.
  useEffect(() => {
    const payload = pendingImport;
    if (!payload || !Array.isArray(payload.lines) || !payload.lines.length) return;
    let cancelled = false;
    (async () => {
      setBusy(true);
      setError(null);
      try {
        if (payload.region) setRegion(payload.region);
        const specs = payload.lines.map((l, i) => ({ key: `imp${i}`, ...l.spec }));
        const res = await api.post('/api/aws/calculator/rates', { lines: specs });
        if (cancelled) return;
        const got = res.data?.lines || [];
        const next = [];
        const failed = [];
        payload.lines.forEach((l, i) => {
          const r = got[i];
          if (!r || r.error) { failed.push(`${l.label}: ${r?.error || 'tidak ada tarif'}`); return; }
          const s = l.spec;
          next.push({
            id: newId(),
            kind: s.kind, region: s.region || payload.region,
            label: l.label,
            instance_type: s.instance_type, os: s.os, tenancy: s.tenancy,
            pre_installed_sw: s.pre_installed_sw,
            db_engine: s.db_engine, db_edition: s.db_edition,
            deployment_option: s.deployment_option, license_model: s.license_model,
            service_code: s.kind === 'ec2' ? 'AmazonEC2' : s.kind === 'rds' ? 'AmazonRDS'
              : s.kind === 'opensearch' ? 'AmazonES' : s.service_code,
            usage_type: r.resolved?.usage_type || s.usage_type,
            // Sama seperti addLine: satu kunci `operation` saja. Untuk baris
            // usage nilainya bisa null dengan sengaja — itu berarti katalog
            // memang menyimpan NULL, dan validator impor hanya meloloskannya
            // bila usage_type itu punya tepat satu grup operation.
            operation: r.resolved?.operation ?? s.operation ?? null,
            // Asal-usul baris usage. HANYA terisi untuk baris yang datang dari
            // impor; baris yang dirakit manual lewat Add line tidak punya
            // usage_key dan dibiarkan null. Menurunkannya balik dari usage_type
            // berarti membalik USAGE_MAP, dan tabel itu ditulis eksplisit di
            // backend justru karena arah itu tidak bisa ditafsirkan.
            usage_key: l.usage_key || null,
            qty: Number(l.qty) || 1,
            hoursPerMonth,
            volumeGb: Number(l.volumeGb) || 0,
            choice: { kind: 'od' },
            rates: r,
            // Jejak asal baris. Dibawa supaya kutipan sumber tetap bisa
            // diperiksa setelah panel impor ditinggalkan; tidak dipakai oleh
            // aritmatika mana pun.
            importedFrom: l.source_text || null,
            importedDefaults: l.defaults?.length ? l.defaults : null,
          });
        });
        setLines(next);
        setSelectedId(next.length ? next[0].id : null);
        if (failed.length) setError(`${failed.length} baris impor tidak bisa dihargai — ${failed.join(' · ')}`);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || e.message);
      } finally {
        if (!cancelled) { setBusy(false); onPendingConsumed?.(); }
      }
    })();
    return () => { cancelled = true; };
    // Deps sengaja hanya [pendingImport]. Payload adalah objek baru setiap kali
    // tombol impor ditekan, jadi identitasnya sudah menjadi penanda "ada yang
    // baru". Menambahkan hoursPerMonth atau callback ke deps akan menyerap
    // ulang payload yang sama ketika config selesai dimuat.
  }, [pendingImport]);

  // Region dilaporkan ke induk supaya tab Import membuka dengan region yang
  // sama dengan keranjang. Satu arah — induk tidak pernah memaksakannya balik.
  useEffect(() => { onRegionChange?.(region); }, [region, onRegionChange]);

  const patchLine = (id, patch) =>
    setLines(prev => prev.map(l => (l.id === id ? { ...l, ...patch } : l)));
  const removeLine = (id) => {
    setLines(prev => prev.filter(l => l.id !== id));
    setSelectedId(prev => (prev === id ? null : prev));
  };

  const selected = lines.find(l => l.id === selectedId) || null;

  // Memuat estimate MENGGANTI seluruh keranjang, tidak menggabungkan. Menggabung
  // akan menghasilkan keranjang campuran yang tidak sama dengan estimate mana pun
  // dan tidak bisa disimpan kembali dengan jujur.
  const onEstimateLoaded = ({ lines: loaded, estimate }) => {
    setLines(loaded);
    setSelectedId(loaded.length ? loaded[0].id : null);
    if (estimate) {
      if (estimate.default_region) setRegion(estimate.default_region);
      if (estimate.vat_country) setVatCountry(estimate.vat_country);
      if (estimate.support_plan) setSupportPlan(estimate.support_plan);
    }
  };

  // ── Total ────────────────────────────────────────────────────────────────
  // Tab usage hanya ditampilkan bila katalognya benar-benar punya isi untuk
  // kelompok itu. Tab kosong mengundang pengguna mengklik lalu menemukan
  // dropdown hampa, dan itu tampak seperti kerusakan.
  // Saat `opts` masih kosong (belum dimuat, atau baru direset karena region /
  // kind berganti) daftar ini WAJIB memuat seluruh USAGE_TABS. Sebelumnya
  // fallback-nya menghilangkan 'other', sehingga mengklik tab Other membuat
  // tab itu lenyap sekejap lalu efek pemulihan di bawah menggeser `kind` ke
  // 'storage'. Fallback yang tidak lengkap = tab hilang + pilihan tergeser.
  const visibleUsageTabs = useMemo(() => {
    const all = opts?.usage_types || [];
    if (!all.length) return USAGE_TABS;
    const present = new Set(all.map(u => groupOfJob(u.job_key)));
    return USAGE_TABS.filter(g => present.has(g));
  }, [opts]);

  // Kategori (job_key) yang tersedia pada tab aktif.
  const usageCategories = useMemo(() => {
    if (!USAGE_TABS.includes(kind)) return [];
    const m = new Map();
    (opts?.usage_types || []).forEach(u => {
      if (groupOfJob(u.job_key) !== kind) return;
      m.set(u.job_key, (m.get(u.job_key) || 0) + 1);
    });
    return [...m.entries()].sort((a, z) => jobLabel(a[0]).localeCompare(jobLabel(z[0])))
      .map(([k, n]) => ({ value: k, label: jobLabel(k), count: n }));
  }, [opts, kind]);

  // Usage type pada tab aktif, dipersempit kategori bila dipilih.
  const usageOptions = useMemo(() => {
    if (!USAGE_TABS.includes(kind)) return [];
    return (opts?.usage_types || [])
      .filter(u => groupOfJob(u.job_key) === kind)
      .filter(u => !draft.category || u.job_key === draft.category)
      .map(u => ({
        value: usageKey(u.usage_type, u.operation),
        // Nama service dibuang dari label: sudah tersirat dari kategori, dan
        // ruangnya lebih berguna untuk usage type + keterangannya.
        label: u.operation ? `${u.usage_type} · ${u.operation}` : u.usage_type,
        sub: u.description && u.description !== u.usage_type ? u.description : undefined,
      }));
  }, [opts, kind, draft.category]);

  // Tab aktif bisa lenyap saat region berganti — mis. 'monitoring' ada di
  // Jakarta tapi belum disync di region lain. Membiarkannya membuat editor
  // menampilkan form kosong tanpa tab yang tersorot.
  //
  // Penjaga `!opts` penting: efek ini hanya boleh memindahkan pilihan
  // berdasarkan katalog NYATA. Berganti tab memicu setOpts(null) lalu fetch
  // async, dan tanpa penjaga ini efek berjalan di jendela transisi itu —
  // menyimpulkan tab yang baru diklik "tidak ada" lalu memantulkan pengguna
  // ke tab pertama. Katalog kosong bukan bukti tab tidak tersedia.
  useEffect(() => {
    if (!opts) return;
    if (!USAGE_TABS.includes(kind)) return;
    if (visibleUsageTabs.includes(kind)) return;
    setKind(visibleUsageTabs[0] || 'ec2');
  }, [kind, opts, visibleUsageTabs]);

  const totals = useMemo(() => {
    let recurring = 0, amortized = 0, cash = 0;
    lines.forEach(l => {
      const c = computeLine(l, hoursPerMonth);
      recurring += c.recurring;
      amortized += c.amortized;
      cash += c.cashUpfront;
    });
    // Basis Support MENGECUALIKAN upfront RI — itu keputusan yang sudah dikunci,
    // bukan penyederhanaan. Karena itu `recurring` yang dipakai, bukan usage.
    const support = lines.length ? supportCost(recurring, tiers, supportPlan) : 0;
    const vatPct = vatCountry === 'ID' ? Number(config?.map?.vat_id) || 0
      : vatCountry === 'SG' ? Number(config?.map?.vat_sg) || 0 : 0;
    // Konvensi mengikuti AWS Pricing Calculator: "Monthly" adalah biaya BERULANG
    // saja, tidak diamortisasi. Upfront ditampilkan terpisah dan hanya sekali di
    // bulan 0 (lihat `cash` di bawah), bukan disebar ke Total per bulan.
    const vat = (recurring + support) * (vatPct / 100);
    const total = recurring + support + vat;
    // Informatif saja, TIDAK termasuk Total per bulan: dipakai untuk membandingkan
    // opsi pembayaran secara adil (All Upfront vs No Upfront) karena keduanya
    // dibagi rata ke seluruh masa sewa.
    const amortizedTotal = recurring + amortized;
    return { recurring, amortized, amortizedTotal, cash, support, vatPct, vat, total };
  }, [lines, tiers, supportPlan, vatCountry, config, hoursPerMonth]);


  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 text-sm p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> {t('awsp.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Kontrol global */}
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
        <Field label={t('awsp.region')}>
          <select value={region} onChange={e => setRegion(e.target.value)} className={selCls}>
            {REGIONS.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
        </Field>
        <Field label={t('awsp.calc.supportPlan')}>
          <select value={supportPlan} onChange={e => setSupportPlan(e.target.value)} className={selCls}>
            <option value="none">{t('awsp.calc.supportNone')}</option>
            <option value="business">Business</option>
            <option value="developer">Developer</option>
            <option value="enterprise_ondemand">Enterprise On-Ramp</option>
          </select>
        </Field>
        <Field label={t('awsp.calc.vat')}>
          <select value={vatCountry} onChange={e => setVatCountry(e.target.value)} className={selCls}>
            <option value="ID">{t('awsp.calc.vatId')} ({config?.map?.vat_id || 0}%)</option>
            <option value="SG">{t('awsp.calc.vatSg')} ({config?.map?.vat_sg || 0}%)</option>
            <option value="none">{t('awsp.calc.vatNone')}</option>
          </select>
        </Field>
        <div className="flex items-center gap-3 ml-auto">
          {/* Ekspor memotret keranjang yang SEDANG tampil, bukan estimate
              tersimpan — angkanya persis yang dilihat pengguna di layar. */}
          <AwsPricingExport
            t={t}
            lines={lines}
            totals={totals}
            region={region}
            supportPlan={supportPlan}
            vatCountry={vatCountry}
            fxRate={fxRate}
            hoursPerMonth={hoursPerMonth}
            computeLine={computeLine}
            choiceLabel={choiceLabel}
          />
          <div className="text-xs text-gray-500">
            {t('awsp.calc.hoursNote')} <span className="font-medium text-gray-700">{hoursPerMonth}</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}

      <AwsPricingEstimates
        lines={lines}
        region={region}
        vatCountry={vatCountry}
        supportPlan={supportPlan}
        fxRate={fxRate}
        findCell={findCell}
        onLoaded={onEstimateLoaded}
      />

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        {/* ── Panel kiri: keranjang ───────────────────────────────────────── */}
        <div className="xl:col-span-3 space-y-3">
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2 text-sm font-medium text-gray-700">
              <ShoppingCart className="w-4 h-4 text-emerald-600" />
              {t('awsp.calc.cart')} <span className="text-gray-400">({lines.length})</span>
            </div>
            {!lines.length && (
              <p className="p-3 text-xs text-gray-500">{t('awsp.calc.cartEmpty')}</p>
            )}
            <ul className="divide-y divide-gray-100">
              {lines.map(l => {
                const c = computeLine(l, hoursPerMonth);
                const on = l.id === selectedId;
                return (
                  <li key={l.id}>
                    <button
                      onClick={() => setSelectedId(l.id)}
                      className={`w-full text-left px-3 py-2 hover:bg-gray-50 ${on ? 'bg-emerald-50' : ''}`}
                    >
                      <div className="flex items-start gap-2">
                        {l.kind === 'ec2'
                          ? <Server className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                          : l.kind === 'rds'
                          ? <Database className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                          : l.kind === 'opensearch'
                          ? <Search className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                          : <HardDrive className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-gray-800 truncate">{l.label}</p>
                          <p className="text-xs text-gray-500">
                            {l.kind === 'usage' ? t('awsp.calc.onDemand') : choiceLabel(l.choice, t)}
                            {l.kind === 'usage' ? '' : ` · ×${l.qty}`}
                          </p>
                          <p className="text-xs font-medium text-gray-700 mt-0.5">
                            ${money(c.recurring)}<span className="text-gray-400">/{t('awsp.mo')}</span>
                            {c.cashUpfront > 0 && (
                              <span className="text-gray-400"> · +${money(c.cashUpfront, 0)} {t('awsp.calc.oneTime')}</span>
                            )}
                            {c.unavailable && (
                              <span className="ml-1 text-amber-600">· {t('awsp.calc.noPrice')}</span>
                            )}
                          </p>
                          {l.drift && (
                            <p className="text-xs text-amber-700 mt-0.5 flex items-center gap-1">
                              <History className="w-3 h-3 shrink-0" />
                              {l.drift}
                            </p>
                          )}
                        </div>
                        <ChevronRight className={`w-4 h-4 shrink-0 ${on ? 'text-emerald-600' : 'text-gray-300'}`} />
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Total — mengikuti konvensi AWS Pricing Calculator: Total per bulan
              hanya biaya berulang, Upfront terpisah dan sekali di bulan 0. */}
          <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-1.5 text-sm">
            <Row label={t('awsp.calc.recurring')} value={`$${money(totals.recurring)}`} bold />
            <Row
              label={supportPlan === 'none' ? t('awsp.calc.support') : `${t('awsp.calc.support')} (${supportPlan})`}
              value={`$${money(totals.support)}`}
            />
            <Row label={`${t('awsp.calc.vat')} ${totals.vatPct}%`} value={`$${money(totals.vat)}`} />
            <div className="border-t border-gray-200 pt-1.5">
              <Row label={t('awsp.calc.totalMonthly')} value={`$${money(totals.total)}`} bold />
              {fxRate > 0 && (
                <p className="text-xs text-gray-500 text-right">≈ Rp {idr(totals.total * fxRate)}</p>
              )}
              <p className="text-xs text-gray-400">{t('awsp.calc.totalMonthlyNote')}</p>
            </div>
            {totals.cash > 0 && (
              <div className="border-t border-gray-200 pt-1.5">
                <Row label={t('awsp.calc.cashUpfront')} value={`$${money(totals.cash)}`} />
                <p className="text-xs text-gray-500">{t('awsp.calc.cashNote')}</p>
              </div>
            )}
            {totals.amortized > 0 && (
              <div className="border-t border-gray-200 pt-1.5">
                <Row label={t('awsp.calc.amortizedCompare')} value={`$${money(totals.amortizedTotal)}`} />
                <p className="text-xs text-gray-400">{t('awsp.calc.amortizedCompareNote')}</p>
              </div>
            )}
            <p className="text-xs text-gray-400 pt-1">{t('awsp.calc.supportBasisNote')}</p>
          </div>
        </div>


        {/* ── Panel tengah: editor baris ──────────────────────────────────── */}
        <div className="xl:col-span-5 space-y-3">
          <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Plus className="w-4 h-4 text-emerald-600" /> {t('awsp.calc.addLine')}
            </div>

            <div className="flex flex-wrap gap-1.5">
              {['ec2', 'rds', 'opensearch', ...visibleUsageTabs].map(k => (
                <button
                  key={k}
                  onClick={() => setKind(k)}
                  className={`px-2.5 py-1.5 rounded text-xs font-medium border ${
                    kind === k ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300'}`}
                >
                  {k === 'ec2' ? t('awsp.calc.kindEc2')
                    : k === 'rds' ? t('awsp.calc.kindRds')
                    : k === 'opensearch' ? t('awsp.calc.kindOpenSearch')
                    : tt(`awsp.calc.kind_${k}`, tabFallback[k])}
                </button>
              ))}
            </div>

            {kind === 'ec2' ? (
              <div className="grid grid-cols-2 gap-2">
                <Field label={t('awsp.instanceType')}>
                  <SearchableSelect
                    value={draft.instance_type}
                    onChange={v => onPick('instance_type', v)}
                    options={ec2Opts('instance_type')}
                    clearable
                    placeholder={t('awsp.searchPlaceholder')}
                  />
                </Field>
                <Field label={t('awsp.os')}>
                  <SearchableSelect
                    value={draft.os}
                    onChange={v => onPick('os', v)}
                    options={ec2Opts('os')}
                    clearable
                    placeholder={t('awsp.searchPlaceholder')}
                    disabled={!draft.instance_type}
                  />
                </Field>
                <Field label={t('awsp.tenancy')}>
                  <SearchableSelect
                    value={draft.tenancy}
                    onChange={v => onPick('tenancy', v)}
                    options={ec2Opts('tenancy')}
                    clearable
                    placeholder={t('awsp.searchPlaceholder')}
                    disabled={!draft.os}
                  />
                </Field>
                <Field label={t('awsp.preInstalledSw')}>
                  <SearchableSelect
                    value={draft.pre_installed_sw}
                    onChange={v => onPick('pre_installed_sw', v)}
                    options={ec2Opts('pre_installed_sw')}
                    clearable
                    placeholder="NA"
                    disabled={!draft.tenancy}
                  />
                </Field>
                <Field label={t('awsp.calc.qty')}>
                  <input type="number" min="1" value={draft.qty}
                    onChange={e => setDraft(d => ({ ...d, qty: e.target.value }))} className={inpCls} />
                </Field>
              </div>
            ) : kind === 'rds' ? (
              <div className="grid grid-cols-2 gap-2">
                <Field label={t('awsp.calc.dbEngine')}>
                  <SearchableSelect
                    value={draft.db_engine}
                    onChange={v => onPickRds('db_engine', v)}
                    options={(opts?.db_engine || []).map(o => ({ value: o.value, label: o.value, count: o.count }))}
                    placeholder={t('awsp.searchPlaceholder')}
                  />
                </Field>
                <Field label={t('awsp.calc.dbEdition')}>
                  <SearchableSelect
                    value={draft.db_edition}
                    onChange={v => onPickRds('db_edition', v)}
                    options={(opts?.db_edition || []).map(o => ({ value: o.value, label: o.value, count: o.count }))}
                    placeholder={t('awsp.calc.dbEditionNone')}
                    disabled={!draft.db_engine || (opts?.db_edition || []).length === 0}
                  />
                </Field>
                <Field label={t('awsp.calc.deployment')}>
                  <SearchableSelect
                    value={draft.deployment_option}
                    onChange={v => onPickRds('deployment_option', v)}
                    options={(opts?.deployment_option || []).map(o => ({ value: o.value, label: o.value, count: o.count }))}
                    placeholder={t('awsp.searchPlaceholder')}
                    disabled={!rdsDone('db_engine') || !rdsDone('db_edition')}
                  />
                </Field>
                <Field label={t('awsp.calc.licenseModel')}>
                  <SearchableSelect
                    value={draft.license_model}
                    onChange={v => onPickRds('license_model', v)}
                    options={(opts?.license_model || []).map(o => ({ value: o.value, label: o.value, count: o.count }))}
                    placeholder={t('awsp.searchPlaceholder')}
                    disabled={!rdsDone('deployment_option')}
                  />
                </Field>
                <Field label={t('awsp.instanceType')}>
                  <SearchableSelect
                    value={draft.instance_type}
                    onChange={v => onPickRds('instance_type', v)}
                    options={(opts?.instance_type || []).map(o => ({ value: o.value, label: o.value, count: o.count }))}
                    placeholder={t('awsp.searchPlaceholder')}
                    disabled={!rdsDone('deployment_option') || !rdsDone('license_model')}
                  />
                </Field>
                <Field label={t('awsp.calc.qty')}>
                  <input type="number" min="1" value={draft.qty}
                    onChange={e => setDraft(d => ({ ...d, qty: e.target.value }))} className={inpCls} />
                </Field>
                <p className="col-span-2 text-xs text-gray-500">{t('awsp.calc.rdsMultiAzNote')}</p>
              </div>
            ) : kind === 'opensearch' ? (
              <div className="grid grid-cols-2 gap-2">
                <Field label={t('awsp.instanceType')}>
                  <SearchableSelect
                    value={draft.instance_type}
                    onChange={v => setDraft(d => ({ ...d, instance_type: v }))}
                    options={(opts?.instance_type || []).map(o => ({ value: o.value, label: o.value, count: o.count }))}
                    placeholder={t('awsp.searchPlaceholder')}
                  />
                </Field>
                <Field label={t('awsp.calc.qty')}>
                  <input type="number" min="1" value={draft.qty}
                    onChange={e => setDraft(d => ({ ...d, qty: e.target.value }))} className={inpCls} />
                </Field>
                <p className="col-span-2 text-xs text-gray-500">{t('awsp.calc.openSearchNote')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Field label={tt('awsp.calc.usageCategory', 'Category')}>
                  <SearchableSelect
                    value={draft.category}
                    onChange={v => setDraft(d => ({ ...d, category: v, usage_type: '', operation: '', service_code: '' }))}
                    options={usageCategories}
                    allLabel={tt('awsp.calc.allCategories', 'All in this tab')}
                    placeholder={t('awsp.searchPlaceholder')}
                  />
                </Field>
                <Field label={t('awsp.calc.usageType')}>
                  <SearchableSelect
                    wide
                    value={usageKey(draft.usage_type, draft.operation)}
                    onChange={v => {
                      // Nilai pilihan adalah "usage_type|operation", bukan
                      // usage_type saja: AWSELB memakai satu usage_type
                      // ('APS4-LoadBalancerUsage') untuk empat jenis load
                      // balancer dengan tarif berbeda, dan hanya operation yang
                      // membedakannya.
                      const row = (opts?.usage_types || [])
                        .find(u => usageKey(u.usage_type, u.operation) === v);
                      setDraft(d => ({
                        ...d,
                        usage_type: row?.usage_type || '',
                        operation: row?.operation || '',
                        service_code: row?.service_code || '',
                      }));
                    }}
                    options={usageOptions}
                    placeholder={t('awsp.searchPlaceholder')}
                  />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label={t('awsp.calc.usageAmount')}>
                    <input type="number" min="0" value={draft.volumeGb}
                      onChange={e => setDraft(d => ({ ...d, volumeGb: e.target.value }))} className={inpCls} />
                  </Field>
                  <Field label={t('awsp.calc.qty')}>
                    <input type="number" min="1" value={draft.qty}
                      onChange={e => setDraft(d => ({ ...d, qty: e.target.value }))} className={inpCls} />
                  </Field>
                </div>
                {draft.service_code === 'AWSELB' && (
                  <p className="text-xs text-gray-500">{t('awsp.calc.elbNote')}</p>
                )}
                {draft.category === 'nat-gateway' && (
                  <p className="text-xs text-gray-500">
                    {tt('awsp.calc.natNote',
                      'A NAT Gateway bills as two lines: NatGateway-Hours per hour and NatGateway-Bytes per GB '
                      + 'processed. Enter hours/month (e.g. 730) for the first, and GB processed for the second. '
                      + 'Both rates happen to be identical in this region — that is a coincidence, not a rule.')}
                  </p>
                )}
              </div>
            )}

            <button
              onClick={addLine}
              disabled={busy}
              className="w-full px-3 py-2 rounded bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {t('awsp.calc.addToCart')}
            </button>
          </div>

          {/* Detail baris terpilih */}
          {selected && (
            <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{selected.label}</p>
                  <p className="text-xs text-gray-500 font-mono truncate">
                    {selected.usage_type}{selected.operation ? ` · ${selected.operation}` : ''}
                  </p>
                </div>
                <button onClick={() => removeLine(selected.id)}
                  className="p-1.5 rounded text-red-600 hover:bg-red-50 shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {selected.snapshot && selected.drift && (
                <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 space-y-0.5">
                  <p className="font-medium flex items-center gap-1">
                    <History className="w-3.5 h-3.5" /> {t('awsp.est.driftTitle')}
                  </p>
                  <p>{t('awsp.est.driftSaved')} ${money(selected.snapshot.unit_price, 4)}/{t('awsp.hr')}
                    {selected.snapshot.upfront > 0 ? ` · ${t('awsp.upfront')} $${money(selected.snapshot.upfront, 0)}` : ''}
                    {selected.snapshot.priced_at ? ` (${selected.snapshot.priced_at})` : ''}</p>
                  <p className="text-amber-700">{selected.drift}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <Field label={t('awsp.calc.qty')}>
                  <input type="number" min="0" value={selected.qty}
                    onChange={e => patchLine(selected.id, { qty: Number(e.target.value) || 0 })} className={inpCls} />
                </Field>
                {selected.kind !== 'usage' ? (
                  <Field label={t('awsp.calc.hoursPerMonth')}>
                    <input type="number" min="0" max="744" value={selected.hoursPerMonth}
                      onChange={e => patchLine(selected.id, { hoursPerMonth: Number(e.target.value) || 0 })} className={inpCls} />
                  </Field>
                ) : (
                  <Field label={`${t('awsp.calc.usageAmount')}${
                    selected.rates?.tiers?.[0]?.unit ? ` (${selected.rates.tiers[0].unit})` : ''}`}>
                    <input type="number" min="0" value={selected.volumeGb}
                      onChange={e => patchLine(selected.id, { volumeGb: Number(e.target.value) || 0 })} className={inpCls} />
                  </Field>
                )}
              </div>

              {selected.kind === 'usage' && (
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1">{t('awsp.calc.tiers')}</p>
                  <table className="w-full text-xs">
                    <tbody>
                      {(selected.rates?.tiers || []).map((tr, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="py-1 text-gray-600">
                            {tr.begin} – {tr.end === null ? '∞' : tr.end} {tr.unit}
                          </td>
                          <td className="py-1 text-right font-mono text-gray-800">${tr.price_per_unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Panel kanan: matriks komitmen ───────────────────────────────── */}
        <div className="xl:col-span-4">
          {!selected && (
            <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
              {t('awsp.calc.pickLine')}
            </div>
          )}
          {selected && selected.kind === 'usage' && (
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600 flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
              {t('awsp.calc.noCommitment')}
            </div>
          )}
          {selected && selected.kind !== 'usage' && (
            <CommitmentMatrix line={selected} hoursPerMonth={hoursPerMonth}
              onChoose={(choice) => patchLine(selected.id, { choice })} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MATRIKS KOMITMEN
// ─────────────────────────────────────────────────────────────────────────────
function CommitmentMatrix({ line, hoursPerMonth, onChoose }) {
  const { t } = useLang();
  const r = line.rates;
  const od = Number(r?.onDemand?.hourly) || 0;
  const qty = Number(line.qty) || 1;
  const hours = Number(line.hoursPerMonth ?? hoursPerMonth) || 730;

  const rows = useMemo(() => {
    const cells = r?.matrix || [];
    const out = [];
    // RDS dan OpenSearch tidak tercakup Savings Plans, jadi dua grup SP dihilangkan sama
    // sekali — bukan ditampilkan sebagai 12 baris "tidak tersedia". Backend
    // sudah tidak mengirim selnya (buildCommitmentMatrix includeSp:false);
    // menampilkan barisnya di sini akan memberi kesan katalognya bolong.
    const groups = [
      { kind: 'ri', tag: 'standard',    label: t('awsp.matrix.riStd') },
      { kind: 'ri', tag: 'convertible', label: t('awsp.matrix.riConv') },
      ...(line.kind === 'rds' || line.kind === 'opensearch' ? [] : [
        { kind: 'sp', tag: 'Compute',     label: t('awsp.matrix.spCompute') },
        { kind: 'sp', tag: 'EC2Instance', label: t('awsp.matrix.spEc2') },
      ]),
    ];
    for (const g of groups) {
      for (const lease of ['1yr', '3yr']) {
        for (const payment of ['No Upfront', 'Partial Upfront', 'All Upfront']) {
          const cell = cells.find(c => c.kind === g.kind
            && c.lease_length === lease && c.payment === payment
            && (g.kind === 'ri' ? c.offering === g.tag : c.plan_type === g.tag));
          const choice = g.kind === 'ri'
            ? { kind: 'ri', offering: g.tag, lease, payment }
            : { kind: 'sp', plan_type: g.tag, lease, payment };
          if (!cell || cell.missing) {
            out.push({ group: g.label, lease, payment, choice, missing: true });
            continue;
          }
          const years = LEASE_YEARS(lease);
          const upfront = (Number(cell.upfront) || 0) * qty;
          // Konvensi AWS Pricing Calculator: "Monthly" adalah biaya BERULANG
          // saja, tidak diamortisasi — Upfront ditampilkan terpisah dan hanya
          // sekali. Jangan gabungkan upfront/bulan ke sini lagi.
          const monthly = (Number(cell.hourly) || 0) * hours * qty;
          // Total untuk SELURUH masa sewa, bukan tahun pertama saja. Untuk 1yr
          // keduanya kebetulan sama; untuk 3yr, upfront + monthly×12 saja akan
          // meremehkan biaya sebesar dua tahun recurring yang hilang.
          const leaseTotal = upfront + monthly * 12 * years;
          const odLeaseTotal = od * hours * 12 * years * qty;
          out.push({
            group: g.label, lease, payment, choice,
            upfront, monthly, leaseTotal,
            saving: odLeaseTotal > 0 ? (1 - leaseTotal / odLeaseTotal) * 100 : null,
            derived: cell.derived, anomaly: cell.anomaly,
          });
        }
      }
    }
    return out;
  }, [r, qty, hours, od, t, line.kind]);


  const active = line.choice || { kind: 'od' };
  const isActive = (c) => active.kind === c.kind
    && active.lease === c.lease && active.payment === c.payment
    && (c.kind === 'ri' ? active.offering === c.offering : active.plan_type === c.plan_type);

  const anomalies = rows.filter(x => x.anomaly).length;

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-100">
        <p className="text-sm font-medium text-gray-700">{t('awsp.matrix.title')}</p>
        <p className="text-xs text-gray-500">{t('awsp.matrix.subtitle')}</p>
      </div>

      {/* On-Demand sebagai baris dasar */}
      <button
        onClick={() => onChoose({ kind: 'od' })}
        className={`w-full text-left px-3 py-2 border-b border-gray-100 hover:bg-gray-50 ${
          active.kind === 'od' ? 'bg-emerald-50' : ''}`}
      >
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-gray-800">{t('awsp.calc.onDemand')}</span>
          <span className="font-mono text-gray-700">
            ${money(od, 4)}/{t('awsp.hr')} · ${money(od * hours * qty)}/{t('awsp.mo')}
          </span>
        </div>
      </button>

      {anomalies > 0 && (
        <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          {t('awsp.matrix.anomalyWarn').replace('{n}', String(anomalies))}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">{t('awsp.matrix.option')}</th>
              <th className="px-2 py-1.5 text-right font-medium">{t('awsp.matrix.upfront')}</th>
              <th className="px-2 py-1.5 text-right font-medium">{t('awsp.matrix.monthly')}</th>
              <th className="px-2 py-1.5 text-right font-medium">{t('awsp.matrix.leaseTotal')}</th>
              <th className="px-2 py-1.5 text-right font-medium">{t('awsp.matrix.saving')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((x, i) => {
              const on = isActive(x.choice);
              const newGroup = i === 0 || rows[i - 1].group !== x.group;
              return (
                <React.Fragment key={i}>
                  {newGroup && (
                    <tr className="bg-gray-100">
                      <td colSpan={5} className="px-2 py-1 font-medium text-gray-600">{x.group}</td>
                    </tr>
                  )}
                  <tr
                    onClick={() => !x.missing && onChoose(x.choice)}
                    className={`border-t border-gray-100 ${
                      x.missing ? 'text-gray-300' : 'cursor-pointer hover:bg-gray-50'} ${on ? 'bg-emerald-50' : ''}`}
                  >
                    <td className="px-2 py-1.5">
                      {x.lease} · {x.payment}
                      {x.derived && <span className="ml-1 text-gray-400" title={t('awsp.matrix.derivedHint')}>†</span>}
                      {x.anomaly && <AlertTriangle className="inline w-3 h-3 ml-1 text-amber-600" title={x.anomaly} />}
                    </td>
                    {x.missing ? (
                      <td colSpan={4} className="px-2 py-1.5 text-center">{t('awsp.matrix.missing')}</td>
                    ) : (
                      <>
                        <td className="px-2 py-1.5 text-right font-mono">
                          {x.upfront > 0 ? `$${money(x.upfront, 0)}` : '—'}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono">${money(x.monthly)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">${money(x.leaseTotal, 0)}</td>
                        <td className={`px-2 py-1.5 text-right font-medium ${
                          x.saving > 0 ? 'text-emerald-700' : 'text-gray-400'}`}>
                          {x.saving === null ? '—' : `${x.saving.toFixed(0)}%`}
                        </td>
                      </>
                    )}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-3 py-2 border-t border-gray-100 text-xs text-gray-500 space-y-1">
        <p>† {t('awsp.matrix.derivedNote')}</p>
        <p>{t('awsp.matrix.savingNote')}</p>
      </div>
    </div>
  );
}

// ── Potongan UI kecil ──────────────────────────────────────────────────────
const selCls = 'w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white disabled:bg-gray-100 disabled:text-gray-400';
const inpCls = 'w-full px-2 py-1.5 rounded border border-gray-300 text-sm';

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className={bold ? 'font-medium text-gray-800' : 'text-gray-600'}>{label}</span>
      <span className={`font-mono ${bold ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{value}</span>
    </div>
  );
}

function choiceLabel(choice, t) {
  if (!choice || choice.kind === 'od') return t('awsp.calc.onDemand');
  if (choice.kind === 'ri') return `RI ${choice.offering} ${choice.lease} ${choice.payment}`;
  return `SP ${choice.plan_type} ${choice.lease} ${choice.payment}`;
}
